import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';
import { LanguageService } from '../../core/services/language.service';
import {
	IFilter,
	IMenu,
	ITab,
	ITableCell,
	ITableConfig,
	ITableHeader,
	ITableRow,
	ITableSortEvent,
	IToolbar,
} from '@tecalisdev/infinity';
import { MasterService } from '../../shared/services/master.service';
import { resolveMatchDataUserMessageFromKoError } from '../../shared/utils/match-data-user-message.util';
import {
	IMedia,
	IViewMedia,
	resolveDownloadFileNameFromUrl,
	resolveViewMediaTypeFromUrl,
} from '../../shared/models/view-media.models';
import {
	DOCS_TYPE,
	CONNECTION_TYPE,
	IOperationConnectionsListBody,
	IOperationFormListBody,
	IOperationListBody,
	IOperationTfaListBody,
	isTfaListRow,
	OPERATION_TYPE,
	IOperationBormeDetail,
	IOperationDetailResponse,
	IOperationFormDetail,
	IOperationTfaMessagingDetailData,
	isTfaOperationDetail,
	OPERATION_STATUS,
	TAB_OPERATIONS,
} from '../models/operations.models';
import {
	formatDate,
	notAvailableLabel,
	permanentExpirationLabel,
	truncateUUID,
	withNationalityFlag,
	resolveDniFlagCountryCode,
} from '../../core/utils/utils';
import { createDateRangeFilters } from '../../shared/list/create-date-range-filters.util';
import { resolveTfaChannelLabelKey } from '../utils/tfa-display.util';
import { formatPhoneDisplay } from '../../processes/utils/sent-messages.util';
import { RolePermissionsService } from '../../core/services/role-permissions.service';
import { MenuItemAction } from '../../shared/models/master.models';
import {
	IOperationDocumentsDetail,
	IOperationEvaluationDetail,
	IOperationOcrDetail,
	IOperationPolDataTry,
	IOperationPolDetail,
	isPolDataRowKoMultipleFaces,
} from '../../processes/models/processes.models';
import { ProcessHelperService } from '../../processes/services/process-helper.service';
import { UserActivityTrackerService } from '../../shared/services/user-activity-tracker.service';
import { ErrorsNumbersService } from '../../settings/pages/error-catalog/services/errors-numbers.service';
import { WorkflowsService } from '../../workflows/services/workflows.service';
import { appendFormOperationActivityLogs } from '../utils/form-operation-activity.util';
import { appendDocumentsActivityLogs } from '../utils/documents-operation-activity.util';
import { appendFinancialEvaluationActivityLogs } from '../utils/financial-evaluation-operation-activity.util';
import { appendOcrActivityLogs } from '../utils/ocr-operation-activity.util';
import { appendPolActivityLogs } from '../utils/pol-operation-activity.util';
import {
	appendMessagingActivityLogs,
	normalizeMessagingActivityState,
} from '../utils/messaging-operation-activity.util';
import {
	appendBormeActivityLogs,
	normalizeBormeActivityState,
} from '../utils/borme-operation-activity.util';
import { getPolDataTriesDisplayCount, getPolTriesCountLabel } from '../utils/pol-tries-display.util';
import { collectOcrAdminImageUrls } from '../../processes/utils/ocr-admin-images.util';

@Injectable({
	providedIn: 'root',
})
export class OperationsHelperService {
	trans = inject(TranslateService);
	masterService = inject(MasterService);
	private langSvc = inject(LanguageService);
	private rolePermissions = inject(RolePermissionsService);
	private processHelper = inject(ProcessHelperService);
	private errorsNumbersService = inject(ErrorsNumbersService);
	private userActivityTracker = inject(UserActivityTrackerService);
	private workflowsService = inject(WorkflowsService);

	private ocrDocTypes = [DOCS_TYPE.DNI, DOCS_TYPE.PASSPORT, DOCS_TYPE.NIE];
	private financialEvaluationTypes = [CONNECTION_TYPE.BANK_SCORING, CONNECTION_TYPE.IRPF, CONNECTION_TYPE.WORK_LIFE];
	public operationType?: OPERATION_TYPE;
	public readonly defaultPagination = { page: 1, limit: 25 };
	public readonly formWorkflowOptions = signal<{ id: string; label: string }[]>([]);
	private formWorkflowOptionsLoaded = false;

	/** Carga workflows para el filtro del listado FORM (una sola vez por sesión). */
	public loadFormWorkflowOptions(): void {
		if (this.formWorkflowOptionsLoaded) {
			return;
		}
		this.formWorkflowOptionsLoaded = true;
		this.workflowsService
			.list({ page: '1', per_page: '1000', sort_by: 'alias', sort_order: 'asc' })
			.pipe(catchError(() => of({ data: [], meta: {} as never })))
			.subscribe((response) => {
				this.formWorkflowOptions.set(
					(response.data || []).map((workflow) => ({
						id: workflow.uuid,
						label: workflow.alias,
					})),
				);
			});
	}

	/**
	 * Nº de intentos POL en `data`: filas auto (`lifeTest` no vacío).
	 * POL estática no aporta filas auto (el label usa fallback en `getPolTriesCountLabel`).
	 */
	public getPolDataTriesDisplayCount(data: IOperationPolDataTry[] | undefined | null): number {
		return getPolDataTriesDisplayCount(data);
	}

	/** Etiqueta para el tag «Nº intentos» en detalle POL; sin filas → N/D. */
	public getPolTriesCountLabel(pol: IOperationPolDetail | undefined | null): string {
		return getPolTriesCountLabel(pol, notAvailableLabel(this.trans));
	}

	public getToolbar(operationType?: OPERATION_TYPE, docType?: DOCS_TYPE): Signal<IToolbar> {
		return computed<IToolbar>(() => {
			this.langSvc.currentLang(); // dependencia reactiva: recalcula al cambiar idioma
			const opUpper = operationType?.toUpperCase() as OPERATION_TYPE | undefined;

			if (opUpper === OPERATION_TYPE.CONNECTIONS) {
				return {
					filters: this.buildConnectionsToolbarFilters(),
					fastFilters: this.buildConnectionsFastFilters(),
					search: {
						placeholder: this.trans.instant('OPERATIONS.CONNECTIONS_LIST.SEARCH_PLACEHOLDER'),
						name: 'search',
					},
				};
			}

			if (opUpper === OPERATION_TYPE.FORM) {
				return this.buildFormToolbar();
			}

			const filters: IFilter[] = [
				...createDateRangeFilters({
					fromTitle: this.trans.instant('FILTERS.FROM'),
					toTitle: this.trans.instant('FILTERS.TO'),
					names: { from: 'created_from', to: 'created_to' },
				}),
				{
					type: 'select',
					title: this.trans.instant('FILTERS.NACIONALITY'),
					name: 'nationality',
					placeholder: this.trans.instant('FILTERS.NACIONALITY_PLACEHOLDER'),
					placeholderThin: this.trans.instant('FILTERS.NACIONALITY'),
					options: this.masterService.countries,
					search: true,
				},
				{
					type: 'text',
					title: this.trans.instant('FILTERS.IDENTIFIER'),
					name: 'identifier',
					placeholder: this.trans.instant('FILTERS.IDENTIFIER_PLACEHOLDER'),
				},
				{
					type: 'text',
					title: this.trans.instant('FILTERS.OPERATION_ID'),
					name: 'operation_uuid',
					placeholder: this.trans.instant('FILTERS.OPERATION_ID_PLACEHOLDER'),
				},
				{
					type: 'text',
					title: this.trans.instant('FILTERS.PROCESS_ID'),
					name: 'process_uuid',
					placeholder: this.trans.instant('FILTERS.PROCESS_ID_PLACEHOLDER'),
				},
			];

			// Si hay más de una compañía, agregar filtro de compañía
			if (this.masterService.companies.length > 1 || this.rolePermissions.isSuperadmin()) {
				// Tras fechas, antes de nacionalidad (estado solo en fastFilters)
				filters.splice(2, 0, {
					type: 'select',
					title: this.trans.instant('FILTERS.COMPANY'),
					name: 'company_uuid',
					placeholder: this.trans.instant('FILTERS.COMPANY_PLACEHOLDER'),
					options: this.masterService.companies,
					search: true,
				});
			}

			if (opUpper === OPERATION_TYPE.OCR) {
				if (!docType) {
					// Primer filtro del panel: tipo de documento (estado solo en fastFilters)
					filters.splice(0, 0, {
						type: 'select',
						title: this.trans.instant('FILTERS.TYPE'),
						name: 'document_type',
						placeholder: this.trans.instant('FILTERS.DOCUMENT_TYPE_PLACEHOLDER'),
						placeholderThin: this.trans.instant('FILTERS.TYPE'),
						options: this.masterService.documentTypes.filter((doc) =>
							this.ocrDocTypes.includes(doc.id as DOCS_TYPE),
						),
					});
				} else {
					// Sin filtro de tipo de documento (viene del tab): nacionalidad al inicio del panel
					const nationalityFilterIndex = filters.findIndex((f) => f.name === 'nationality');
					if (nationalityFilterIndex !== -1) {
						const [nationalityFilter] = filters.splice(nationalityFilterIndex, 1);
						filters.splice(0, 0, nationalityFilter);
					}
				}
			}

			if (opUpper === OPERATION_TYPE.POL) {
				filters.splice(0, 0, {
					type: 'select',
					title: this.trans.instant('FILTERS.TYPE'),
					name: 'mode',
					placeholder: this.trans.instant('FILTERS.POL_TYPE_PLACEHOLDER'),
					placeholderThin: this.trans.instant('FILTERS.TYPE'),
					options: [
						{ id: 'auto', label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.POL_MODE_ACTIVE') },
						{ id: 'manual', label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.POL_MODE_STATIC') },
					],
				});
			}

			if (opUpper === OPERATION_TYPE.DOCUMENTS) {
				filters.splice(0, 0, {
					type: 'select',
					title: this.trans.instant('FILTERS.TYPE'),
					name: 'document_type',
					placeholder: this.trans.instant('FILTERS.DOCUMENT_TYPE_PLACEHOLDER'),
					placeholderThin: this.trans.instant('FILTERS.TYPE'),
					options: this.masterService.documentTypes,
				});
			}

			if (opUpper === OPERATION_TYPE.MESSAGING) {
				filters.splice(0, 0, {
					type: 'select',
					title: this.trans.instant('FILTERS.TYPE'),
					name: 'messaging_channel',
					placeholder: this.trans.instant('FILTERS.MESSAGING_CHANNEL_PLACEHOLDER'),
					placeholderThin: this.trans.instant('FILTERS.TYPE'),
					options: [
						{ id: OPERATION_TYPE.SMS, label: this.trans.instant('OPERATIONS.SMS') },
						{ id: OPERATION_TYPE.WHATSAPP, label: this.trans.instant('OPERATIONS.WHATSAPP') },
					],
				});
			}

			if (opUpper === OPERATION_TYPE.FINANCIAL_EVALUATION || opUpper === OPERATION_TYPE.EVALUATION) {
				filters.splice(0, 0, {
					type: 'select',
					title: this.trans.instant('FILTERS.TYPE'),
					name: 'connection_type',
					placeholder: this.trans.instant('FILTERS.FINANCIAL_EVALUATION_TYPE_PLACEHOLDER'),
					placeholderThin: this.trans.instant('FILTERS.TYPE'),
					options: this.masterService.connectionTypes
						.filter((connection) =>
							this.financialEvaluationTypes.includes(connection.id as CONNECTION_TYPE),
						)
						.map((connection) => ({
							id: connection.id,
							label: this.trans.instant(`MASTER.CONNECTION_TYPE.${connection.id}`),
						})),
				});
			}

			return {
				filters,
				fastFilters: this.masterService.processStatusFilter
					? [
							{
								name: 'state_uuid',
								options: this.masterService.processStatusFilter,
								value: '',
							},
						]
					: undefined,
				search: {
					placeholder: this.trans.instant('FILTERS.SEARCH_BY_NAME_OR_ID'),
					name: 'search',
				},
			};
		});
	}

	/** Toolbar listado Formularios (Figma `9257-180134`): workflow, fechas y más filtros; estado en chips. */
	private buildFormToolbar(): IToolbar {
		this.formWorkflowOptions();
		const filters: IFilter[] = [
			{
				type: 'select',
				title: this.trans.instant('FILTERS.WORKFLOW'),
				name: 'workflow_uuid',
				placeholder: this.trans.instant('FILTERS.WORKFLOW_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.WORKFLOW'),
				options: this.formWorkflowOptions(),
				search: true,
			},
			...createDateRangeFilters({
				fromTitle: this.trans.instant('FILTERS.FROM'),
				toTitle: this.trans.instant('FILTERS.TO'),
				names: { from: 'created_from', to: 'created_to' },
			}),
			{
				type: 'select',
				title: this.trans.instant('FILTERS.NACIONALITY'),
				name: 'nationality',
				placeholder: this.trans.instant('FILTERS.NACIONALITY_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.NACIONALITY'),
				options: this.masterService.countries,
				search: true,
			},
			{
				type: 'text',
				title: this.trans.instant('FILTERS.IDENTIFIER'),
				name: 'identifier',
				placeholder: this.trans.instant('FILTERS.IDENTIFIER_PLACEHOLDER'),
			},
			{
				type: 'text',
				title: this.trans.instant('FILTERS.OPERATION_ID'),
				name: 'operation_uuid',
				placeholder: this.trans.instant('FILTERS.OPERATION_ID_PLACEHOLDER'),
			},
			{
				type: 'text',
				title: this.trans.instant('FILTERS.PROCESS_ID'),
				name: 'process_uuid',
				placeholder: this.trans.instant('FILTERS.PROCESS_ID_PLACEHOLDER'),
			},
		];

		if (this.masterService.companies.length > 1 || this.rolePermissions.isSuperadmin()) {
			filters.splice(3, 0, {
				type: 'select',
				title: this.trans.instant('FILTERS.COMPANY'),
				name: 'company_uuid',
				placeholder: this.trans.instant('FILTERS.COMPANY_PLACEHOLDER'),
				options: this.masterService.companies,
				search: true,
			});
		}

		return {
			filters,
			fastFilters: this.masterService.processStatusFilter
				? [
						{
							name: 'state_uuid',
							options: this.masterService.processStatusFilter,
							value: '',
						},
					]
				: undefined,
			search: {
				placeholder: this.trans.instant('OPERATIONS.FORM_LIST.SEARCH_PLACEHOLDER'),
				name: 'search',
			},
		};
	}

	/** Toolbar listado Conexiones: solo filtros acordados (Figma); sin «Resultado»; proveedor como `select` como «Tipo». */
	private buildConnectionsToolbarFilters(): IFilter[] {
		const typeOptions = this.getConnectionsConnectionTypeFilterOptions();

		const filters: IFilter[] = [
			{
				type: 'select',
				title: this.trans.instant('FILTERS.CONNECTION_STATE'),
				name: 'state_uuid',
				placeholder: this.trans.instant('FILTERS.CONNECTION_STATE_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.CONNECTION_STATE'),
				options: this.masterService.processStatusFilter ?? [],
			},
			...createDateRangeFilters({
				fromTitle: this.trans.instant('FILTERS.FROM'),
				toTitle: this.trans.instant('FILTERS.TO'),
				names: { from: 'created_from', to: 'created_to' },
			}),
			{
				type: 'select',
				title: this.trans.instant('FILTERS.TYPE'),
				name: 'connection_type',
				placeholder: this.trans.instant('FILTERS.TYPE_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.TYPE'),
				options: typeOptions,
			},
			{
				type: 'select',
				title: this.trans.instant('FILTERS.CONNECTION_PROVIDER'),
				name: 'provider',
				placeholder: this.trans.instant('FILTERS.CONNECTION_PROVIDER_PLACEHOLDER'),
				placeholderThin: this.trans.instant('FILTERS.CONNECTION_PROVIDER'),
				options: [],
			},
			{
				type: 'text',
				title: this.trans.instant('FILTERS.CONNECTION_ID'),
				name: 'connection_id',
				placeholder: this.trans.instant('FILTERS.CONNECTION_ID_PLACEHOLDER'),
			},
			{
				type: 'text',
				title: this.trans.instant('FILTERS.OPERATION_ID'),
				name: 'operation_uuid',
				placeholder: this.trans.instant('FILTERS.OPERATION_ID_PLACEHOLDER'),
			},
		];

		if (this.masterService.companies.length > 1 || this.rolePermissions.isSuperadmin()) {
			// Tras fechas, antes de tipo (alineado con otros listados)
			filters.splice(3, 0, {
				type: 'select',
				title: this.trans.instant('FILTERS.COMPANY'),
				name: 'company_uuid',
				placeholder: this.trans.instant('FILTERS.COMPANY_PLACEHOLDER'),
				options: this.masterService.companies,
				search: true,
			});
		}

		return filters;
	}

	/** Opciones select «Tipo» (conexiones del maestro; mismas etiquetas i18n hasta lista proveedores en API). */
	private getConnectionsConnectionTypeFilterOptions() {
		return (this.masterService.connectionTypes || []).map((connection) => ({
			id: connection.id,
			label: this.trans.instant(`MASTER.CONNECTION_TYPE.${connection.id}`),
		}));
	}

	/** Chips de tipo de conexión (Infinity `fastFilters`). */
	private buildConnectionsFastFilters():
		| { name: string; options: { id: string; label: string }[]; value: string | string[]; multiple?: boolean }[]
		| undefined {
		const rows: {
			name: string;
			options: { id: string; label: string }[];
			value: string | string[];
			multiple?: boolean;
		}[] = [];

		const typeOpts = (this.masterService.connectionTypes || []).map((c) => ({
			id: String(c.id),
			label: this.getConnectionsChipLabel(String(c.id)),
		}));
		if (typeOpts.length) {
			rows.push({
				name: 'connection_type',
				options: typeOpts,
				value: '',
			});
		}
		return rows.length ? rows : undefined;
	}

	private getConnectionsChipLabel(connectionId: string): string {
		const chipKey = `OPERATIONS.CONNECTIONS_LIST.CHIP.${connectionId}`;
		const chip = this.trans.instant(chipKey);
		if (chip !== chipKey) {
			return chip;
		}
		return this.trans.instant(`MASTER.CONNECTION_TYPE.${connectionId}`);
	}

	get header(): ITableHeader[] {
		if (this.operationType === OPERATION_TYPE.CONNECTIONS) {
			return [
				{ label: this.trans.instant('TABLE.HEADER.CONNECTIONS_TYPE') },
				{ label: this.trans.instant('TABLE.HEADER.CONNECTION_ID') },
				{ label: this.trans.instant('TABLE.HEADER.OPERATION_ID') },
				{ label: this.trans.instant('TABLE.HEADER.COMPANY') },
				{ label: this.trans.instant('TABLE.HEADER.CONNECTION_STATE') },
				{ label: this.trans.instant('TABLE.HEADER.CONNECTION_RESULT') },
				{
					label: this.trans.instant('TABLE.HEADER.DATE_CREATE_ABREV'),
					sortable: true,
					sortKey: 'created_at',
					align: 'right',
				},
			];
		}

		if (this.operationType === OPERATION_TYPE.FORM) {
			return [
				{ label: this.trans.instant('TABLE.HEADER.OPERATION_ID') },
				{ label: this.trans.instant('TABLE.HEADER.NAME') },
				{ label: this.trans.instant('TABLE.HEADER.PROCESS_ID') },
				{ label: this.trans.instant('TABLE.HEADER.STEPS') },
				{ label: this.trans.instant('TABLE.HEADER.COMPANY') },
				{ label: this.trans.instant('TABLE.HEADER.STATE') },
				{
					label: this.trans.instant('TABLE.HEADER.DATE_LAST_UPDATE_ABREV'),
					sortable: true,
					sortKey: 'created_at',
					align: 'right',
				},
			];
		}

		if (this.operationType === OPERATION_TYPE.MESSAGING) {
			return [
				{ label: this.trans.instant('TABLE.HEADER.OPERATION_ID') },
				{ label: this.trans.instant('TABLE.HEADER.PHONE') },
				{ label: this.trans.instant('TABLE.HEADER.PROCESS_ID') },
				{ label: this.trans.instant('TABLE.HEADER.TYPE') },
				{ label: this.trans.instant('TABLE.HEADER.COMPANY') },
				{ label: this.trans.instant('TABLE.HEADER.STATE') },
				{
					label: this.trans.instant('TABLE.HEADER.DATE_CREATE_ABREV'),
					sortable: true,
					sortKey: 'created_at',
					align: 'right',
				},
			];
		}

		const header: ITableHeader[] = [
			{ label: this.trans.instant('TABLE.HEADER.OPERATION_ID') },
			{ label: this.trans.instant('TABLE.HEADER.NAME') },
			{ label: this.trans.instant('TABLE.HEADER.PROCESS_ID') },
			{ label: this.trans.instant('TABLE.HEADER.TYPE') },
			{ label: this.trans.instant('TABLE.HEADER.COMPANY') },
			{ label: this.trans.instant('TABLE.HEADER.STATE') },
			{
				label: this.trans.instant('TABLE.HEADER.DATE_CREATE_ABREV'),
				sortable: true,
				sortKey: 'created_at',
				align: 'right',
			},
		];

		// OCR: TYPE tras NAME (ID sigue primero — NIF-342).
		if (this.operationType === OPERATION_TYPE.OCR) {
			header.splice(2, 0, header.splice(3, 1)[0]);
		}

		return header;
	}

	get config(): ITableConfig {
		return {
			clickeable: true,
			pagination: {
				page: this.defaultPagination.page,
				limit: this.defaultPagination.limit,
				total: 0,
				label: this.trans.instant('TABLE.ELEMENTS_BY_PAGE'),
				elementsByPage: [
					{ id: '25', label: '25' },
					{ id: '50', label: '50' },
					{ id: '100', label: '100' },
				],
			},
		};
	}

	get sort(): ITableSortEvent {
		return { ...this.resetSort() };
	}

	public resetSort(): ITableSortEvent {
		return {
			sortKey: 'created_at',
			sortDirection: 'desc',
			headerIndex: this.header.findIndex((h) => h.sortKey === 'created_at') || 0,
		};
	}

	get tabs(): ITab[] {
		return [
			{ id: '', label: this.trans.instant('OPERATIONS.ALL'), selected: true },
			...this.ocrDocTypes.map((type) => ({
				id: type,
				label: this.trans.instant(`MASTER.DOCUMENT_TYPE.${type}`),
			})),
		];
	}

	public transformRow(response: IOperationListBody[]): ITableRow[] {
		return response.map((row) => this.transformListTableRow(row));
	}

	public transformListTableRow(row: IOperationListBody): ITableRow {
		if (this.operationType === OPERATION_TYPE.FORM) {
			return this.transformFormListTableRow(row as IOperationFormListBody);
		}

		if (this.operationType === OPERATION_TYPE.CONNECTIONS) {
			return this.transformConnectionsListTableRow(row as IOperationConnectionsListBody);
		}

		if (this.operationType === OPERATION_TYPE.MESSAGING) {
			return this.transformMessagingListTableRow(row);
		}

		let typeLabel = this.getTypeTableCell(row);
		if (this.operationType === OPERATION_TYPE.OCR) {
			const docType = String(row['doc_type'] || '').toUpperCase();
			// NIF-341: pasaporte → bandera nacionalidad; NIE → ES; DNI/DNI_* → bandera del país del documento.
			if (docType.includes('PASSPORT')) {
				typeLabel = withNationalityFlag(row['nationality'], typeLabel);
			} else if (docType.includes('NIE') || docType === 'TARJ_RESID') {
				typeLabel = withNationalityFlag('ESP', typeLabel);
			} else if (docType.startsWith('DNI')) {
				typeLabel = withNationalityFlag(
					resolveDniFlagCountryCode(String(row['doc_type'] || ''), row['nationality']),
					this.trans.instant('MASTER.DOCUMENT_TYPE.DNI'),
				);
			} else {
				typeLabel = withNationalityFlag(row['nationality'], typeLabel);
			}
		}

		const cells: ITableCell[] = [
			{ label: truncateUUID(row.uuid), copyText: { labelToCopy: row.uuid } },
			{
				label: this.getListPrimaryLabel(row),
				secondaryLabel: row['id_client'],
			},
			{ label: truncateUUID(row.process_uuid), copyText: { labelToCopy: row.process_uuid } },
			{ label: typeLabel },
			{ label: row.company },
			{
				label: '',
				tag: {
					label: this.trans.instant(`MASTER.STATUS.${row.state_name}`),
					status: this.masterService.getTagStatus(row.state_name),
				},
			},
			{ label: formatDate(row.created_at), align: 'right' },
		];

		// OCR: TYPE tras NAME (ID sigue primero — NIF-342).
		if (this.operationType === OPERATION_TYPE.OCR) {
			cells.splice(2, 0, cells.splice(3, 1)[0]);
		}

		return {
			clickValue: row.uuid,
			cells,
		};
	}

	/** Listado Formularios: columna Pasos con badge; última act. en `updated_at`. */
	private transformFormListTableRow(row: IOperationFormListBody): ITableRow {
		const stepsLabel = this.getFormListStepsLabel(row);
		const empty = notAvailableLabel(this.trans);
		const cells: ITableCell[] = [
			{ label: truncateUUID(row.uuid), copyText: { labelToCopy: row.uuid } },
			{
				label: this.getListPrimaryLabel(row),
				secondaryLabel: row.id_client,
			},
			{ label: truncateUUID(row.process_uuid), copyText: { labelToCopy: row.process_uuid } },
			stepsLabel != null ? { label: '', badge: { label: stepsLabel } } : { label: empty },
			{ label: row.company },
			{
				label: '',
				tag: {
					label: this.trans.instant(`MASTER.STATUS.${row.state_name}`),
					status: this.masterService.getTagStatus(row.state_name),
				},
			},
			{ label: formatDate(row.updated_at || row.created_at), align: 'right' },
		];

		return {
			clickValue: row.uuid,
			cells,
		};
	}

	private transformMessagingListTableRow(row: IOperationListBody): ITableRow {
		const tfaRow = row as IOperationTfaListBody;
		const opUuid = (isTfaListRow(row) ? tfaRow.uuid : row.uuid)?.trim() || '';
		const processUuid = tfaRow.process_uuid ?? '';
		const stateName = tfaRow.state_name ?? '';
		const phoneRaw = tfaRow.phone?.trim() || '';
		const phoneLabel = formatPhoneDisplay(phoneRaw || null, notAvailableLabel(this.trans));

		// Orden de columnas: ID → NAME → PROCESS (NIF-342). Contenido TFA: solo teléfono formateado (NIF-760).
		const cells: ITableCell[] = [
			{ label: truncateUUID(opUuid), copyText: { labelToCopy: opUuid } },
			{ label: phoneLabel },
			{ label: truncateUUID(processUuid), copyText: { labelToCopy: processUuid } },
			{ label: this.getTypeTableCell(row) },
			{ label: tfaRow.company ?? '' },
			stateName
				? {
						label: '',
						tag: {
							label: this.trans.instant(`MASTER.STATUS.${stateName}`),
							status: this.masterService.getTagStatus(stateName),
						},
					}
				: { label: '-' },
			{ label: formatDate(tfaRow.created_at), align: 'right' },
		];

		return {
			clickValue: opUuid,
			cells,
		};
	}

	private getFormListStepsLabel(row: IOperationFormListBody): string | null {
		const steps = row.steps ?? row.total_steps;
		if (steps == null || steps < 0) {
			return null;
		}
		return String(steps);
	}

	/** Etiqueta principal de la columna nombre del listado. */
	private getListPrimaryLabel(row: IOperationListBody): string {
		const name = row['name'];
		if (typeof name === 'string' && name.trim() !== '') {
			return name.trim();
		}
		return notAvailableLabel(this.trans);
	}

	/** Listado Conexiones: columnas y celdas según diseño (tipo + nombre, ids, estado, resultado desde `result`). */
	private transformConnectionsListTableRow(row: IOperationConnectionsListBody): ITableRow {
		const typePrimary = this.getConnectionsTypePrimaryLabel(row);
		const secondary = this.getConnectionsNameSecondary(row);
		const connIdRaw = (row.connection_uuid?.trim() || row.uuid)?.trim() || '';
		const opIdRaw = (row.operation_uuid?.trim() || row.uuid)?.trim() || '';
		const cells: ITableCell[] = [
			{
				label: typePrimary,
				...(secondary ? { secondaryLabel: secondary } : {}),
			},
			{ label: truncateUUID(connIdRaw), copyText: { labelToCopy: connIdRaw } },
			{ label: truncateUUID(opIdRaw), copyText: { labelToCopy: opIdRaw } },
			{ label: row.company },
			this.getConnectionsConnectionStateCell(row),
			this.getConnectionsResultCell(row),
			{ label: formatDate(row.created_at), align: 'right' },
		];
		return {
			clickValue: row.uuid,
			cells,
		};
	}

	private getConnectionsTypePrimaryLabel(row: IOperationConnectionsListBody): string {
		const empty = notAvailableLabel(this.trans);
		const codes =
			row.types
				?.map((t) => (typeof t?.type === 'string' ? t.type.trim() : ''))
				.filter((t): t is string => t.length > 0) ?? [];
		const fromRow =
			codes.length > 0
				? codes
				: (() => {
						const raw = row.connection_type || row.financial_evaluation_type;
						if (raw == null || String(raw).trim() === '') {
							return [] as string[];
						}
						return [String(raw).trim()];
					})();
		if (fromRow.length === 0) {
			return empty;
		}
		const parts = fromRow.map((code) => this.translateConnectionTypeOrDash(code));
		if (parts.every((p) => p === empty)) {
			return empty;
		}
		return parts.join(', ');
	}

	/** Etiqueta traducida `MASTER.CONNECTION_TYPE.*` para un código de conexión; sin traducción → N/D. */
	private translateConnectionTypeOrDash(code: string): string {
		const empty = notAvailableLabel(this.trans);
		const key = this.normalizeConnectionTypeI18nKey(code.trim());
		if (!key) return empty;
		const fullKey = `MASTER.CONNECTION_TYPE.${key}`;
		if (!this.masterService.hasTranslation(fullKey)) return empty;
		return this.trans.instant(fullKey);
	}

	private normalizeConnectionTypeI18nKey(code: string): string {
		if (!code) return '';
		if (code === 'WORK_LIFE' || code === 'work_life') return 'WORKLIFE';
		return code;
	}

	/** Segunda línea columna «Tipo»: nombre; si no viene, proveedor y compañía como respaldo. */
	private getConnectionsNameSecondary(row: IOperationConnectionsListBody): string | undefined {
		const nombre = row.name?.trim();
		if (nombre) return nombre;
		const proveedor = row.provider?.trim();
		if (proveedor) return proveedor;
		const compania = row.company?.trim();
		return compania || undefined;
	}

	private getConnectionsConnectionStateCell(row: IOperationConnectionsListBody): ITableCell {
		return {
			label: '',
			tag: {
				label: this.getConnectionsStateLabelFromMaster(row),
				status: this.masterService.getTagStatus(row.state_name),
			},
		};
	}

	/**
	 * Etiqueta de estado alineada con el filtro «Estado conexión» (`processStatusFilter` / `getNameStatus` del maestro).
	 */
	private getConnectionsStateLabelFromMaster(row: IOperationConnectionsListBody): string {
		const uuid = row.state_uuid?.trim();
		if (uuid) {
			const fromUuid = this.masterService.getNameStatus(uuid);
			if (fromUuid) return fromUuid;
		}
		const nameNorm = String(row.state_name ?? '').toUpperCase();
		const opts = this.masterService.processStatusFilter;
		if (Array.isArray(opts) && nameNorm) {
			const hit = opts.find((o) => String(o.value).toUpperCase() === nameNorm);
			if (hit?.label != null && String(hit.label).trim() !== '') {
				return String(hit.label);
			}
		}
		if (nameNorm) {
			return this.trans.instant(`MASTER.STATUS.${nameNorm}`);
		}
		return notAvailableLabel(this.trans);
	}

	/**
	 * Columna «Resultado»: texto en `result` con `tec-rate-alert` (Infinity Table).
	 * El color sigue `state_name` para mantener el mismo criterio que otras vistas.
	 */
	private getConnectionsResultCell(row: IOperationConnectionsListBody): ITableCell {
		const text = row.result?.trim() ?? '';
		if (!text) return { label: notAvailableLabel(this.trans) };
		return {
			label: '',
			rateAlert: {
				label: text,
				status: this.getRateAlertStatusByStateName(row.state_name),
			},
		};
	}

	/** Mapeo visual DS para `rate-alert` por `state_name` (alineado con otros listados). */
	private getRateAlertStatusByStateName(stateName: string | undefined): 'success' | 'error' | 'warning' | 'neutral' {
		const normalized = String(stateName ?? '').toUpperCase();
		if (normalized === OPERATION_STATUS.OK) return 'success';
		if (normalized === OPERATION_STATUS.KO) return 'error';
		if (normalized === OPERATION_STATUS.IN_PROGRESS) return 'warning';
		return 'neutral';
	}

	/** Texto de celda «Tipo» para tipos de conexión (clave i18n `MASTER.CONNECTION_TYPE.*`). */
	private formatConnectionTypeCell(raw: string | null | undefined): string {
		if (raw == null || String(raw).trim() === '') {
			return notAvailableLabel(this.trans);
		}
		const key = String(raw).trim();
		const i18nKey = key === 'WORK_LIFE' || key === 'work_life' ? 'WORKLIFE' : key;
		return this.trans.instant(`MASTER.CONNECTION_TYPE.${i18nKey}`);
	}

	private getTypeTableCell(row: IOperationListBody): string {
		if (this.operationType) this.operationType = this.operationType.toUpperCase() as OPERATION_TYPE;
		const empty = notAvailableLabel(this.trans);

		// OCR
		if (this.operationType === OPERATION_TYPE.OCR) {
			return row['doc_type'] ? this.masterService.getDocumentTypeLabel(row['doc_type']) : empty;
		}

		// POL
		if (this.operationType === OPERATION_TYPE.POL) {
			if ('mode' in row && row.mode?.trim() !== '') {
				return this.trans.instant(
					`WORKFLOWS.FORM.OPERATIONS.POL_MODE_${row.mode === 'auto' ? 'ACTIVE' : 'STATIC'}`,
				);
			} else {
				return empty;
			}
		}

		// DOCUMENTS
		if (this.operationType === OPERATION_TYPE.DOCUMENTS) {
			if ('types' in row && row?.types?.length > 0) {
				return row.types.map((type) => this.masterService.getNameDocumentType(type.type)).join(', ');
			} else {
				return empty;
			}
		}

		// CONNECTIONS (listado): misma lógica que la columna «Tipo» (solo traducciones `MASTER.CONNECTION_TYPE.*`).
		if (this.operationType === OPERATION_TYPE.CONNECTIONS) {
			return this.getConnectionsTypePrimaryLabel(row as IOperationConnectionsListBody);
		}

		// FINANCIAL_EVALUATION / EVALUATION
		if (
			this.operationType === OPERATION_TYPE.FINANCIAL_EVALUATION ||
			this.operationType === OPERATION_TYPE.EVALUATION
		) {
			return row['financial_evaluation_type']
				? this.formatConnectionTypeCell(String(row['financial_evaluation_type']))
				: empty;
		}

		if (this.operationType === OPERATION_TYPE.BORME) {
			return this.trans.instant('OPERATIONS.BORME');
		}

		if (this.operationType === OPERATION_TYPE.MESSAGING) {
			if (isTfaListRow(row)) {
				const channel = row.channel?.trim().toLowerCase();
				const labelKey = channel ? resolveTfaChannelLabelKey(channel) : '';
				if (labelKey && this.masterService.hasTranslation(labelKey)) {
					return this.trans.instant(labelKey);
				}
			}
			const raw = row?.operation_type_name;
			if (!raw) return empty;
			const key = `OPERATIONS.${raw}`;
			const label = this.trans.instant(key);
			return label !== key ? label : raw;
		}

		return row?.operation_type_name || empty;
	}

	/**
	 * Estados de bloqueo relevantes por tipo: POL → rostro e IP; OCR → documento e IP; resto → solo IP.
	 * Cada dimensión: true si `locks` incluye al menos un registro en la lista correspondiente (API).
	 */
	private getIdentityLockStatesForOperation(detail: IOperationDetailResponse): boolean[] {
		const locks = detail.locks;
		const faceBlocked = (locks?.blocked_faces?.length ?? 0) > 0;
		const documentBlocked = (locks?.blocked_identifiers?.length ?? 0) > 0;
		const ipBlocked = (locks?.blocked_ips?.length ?? 0) > 0;

		const opType = detail.operation_type_name.toUpperCase() as OPERATION_TYPE;
		if (opType === OPERATION_TYPE.POL) {
			return [faceBlocked, ipBlocked];
		}
		if (opType === OPERATION_TYPE.OCR) {
			return [documentBlocked, ipBlocked];
		}
		return [ipBlocked];
	}

	/**
	 * Menú: cambiar estado; bloquear/desbloquear identidad (una opción o ambas si el estado es mixto); ver proceso; ver identidad.
	 */
	public getDetailMenuOpt(detail?: IOperationDetailResponse | null): IMenu[] {
		const base: IMenu[] = [
			{
				label: this.trans.instant('BUTTON.CHANGE_STATUS'),
				icon: 'icon-edit',
				id: MenuItemAction.STATUS,
			},
		];

		const hasIdentity = Boolean(detail?.identity?.uuid?.trim());

		const footer: IMenu[] = [
			{
				label: this.trans.instant('BUTTON.SHOW_PROCESS'),
				icon: 'icon-eye',
				id: MenuItemAction.SHOW_PROCESS,
			},
		];
		if (hasIdentity) {
			footer.push({
				label: this.trans.instant('BUTTON.SHOW_IDENTITY'),
				icon: 'icon-eye',
				id: MenuItemAction.SHOW_IDENTITY,
			});
		}

		const logsMenu: IMenu[] = [];
		if (this.rolePermissions.isSuperadmin() && detail?.logs_url) {
			logsMenu.push({
				label: this.trans.instant('PROCESSES.DETAIL.LOGS'),
				icon: 'icon-open-web',
				id: MenuItemAction.SHOW_LOGS,
			});
		}

		if (!detail?.operation_type_name) {
			return [...base, ...footer, ...logsMenu];
		}

		const identitySection: IMenu[] = [];
		if (hasIdentity) {
			const states = this.getIdentityLockStatesForOperation(detail);
			const allBlocked = states.length > 0 && states.every(Boolean);
			const noneBlocked = states.every((s) => !s);

			if (allBlocked) {
				identitySection.push({
					label: this.trans.instant('PROCESSES.MENU.UNLOCK_IDENTITY'),
					icon: 'icon-unlock',
					id: MenuItemAction.UNLOCK,
				});
			} else if (noneBlocked) {
				identitySection.push({
					label: this.trans.instant('PROCESSES.MENU.BLOCK_IDENTITY'),
					icon: 'icon-lock',
					id: MenuItemAction.BLOCK,
				});
			} else {
				identitySection.push({
					label: this.trans.instant('PROCESSES.MENU.BLOCK_IDENTITY'),
					icon: 'icon-lock',
					id: MenuItemAction.BLOCK,
				});
				identitySection.push({
					label: this.trans.instant('PROCESSES.MENU.UNLOCK_IDENTITY'),
					icon: 'icon-unlock',
					id: MenuItemAction.UNLOCK,
				});
			}
		}

		return [...base, ...identitySection, ...footer, ...logsMenu];
	}

	/** Configuración del visor de media (selfies POL): tag y `detailLabel` por ítem según contrato `IViewMedia` de Infinity. */
	public buildViewMediaSelfieGallery(detail: IOperationDetailResponse, imgSelected: string): IViewMedia {
		const selfies = detail?.data?.images?.selfie;
		const subtitle = this.buildOperationMediaSubtitle(detail);
		const tag = {
			label: this.trans.instant('MASTER.STATUS.' + (detail?.state_name?.toUpperCase() ?? '')),
			status: this.masterService.getTagStatus(detail?.state_name),
		};
		const data: IMedia[] =
			selfies?.map((image: string) => ({
				type: 'image' as const,
				doc: image,
				selected: image === imgSelected,
				scale: 1.0,
			})) ?? [];
		return {
			show: true,
			title: detail?.name ?? '',
			tag,
			subtitle,
			activity: this.buildOperationMediaActivity(detail),
			downloadFileName: `operation_${detail?.uuid ?? 'detail'}_image.jpg`,
			data,
		};
	}

	/**
	 * Etiqueta secundaria del visor de imágenes OCR (p. ej. «Tipo NIE»), usando el tipo de documento en lugar del ID de operación.
	 */
	private buildOcrViewMediaDetailLabel(ocr: IOperationOcrDetail): string {
		const code = ocr?.document_type;
		if (!code) return '';
		const typeLabel = this.masterService.getDocumentTypeLabel(code);
		return this.trans.instant('OPERATIONS.DETAIL.MEDIA_OCR_DOCUMENT_TYPE', { type: typeLabel });
	}

	/**
	 * URLs de documentos en evaluación financiera / conexiones (`data.document[].url`).
	 */
	public getFinancialEvaluationDocumentUrls(detail: IOperationDetailResponse): string[] {
		const opType = detail.operation_type_name?.toUpperCase() as OPERATION_TYPE | undefined;
		if (
			opType !== OPERATION_TYPE.CONNECTIONS &&
			opType !== OPERATION_TYPE.FINANCIAL_EVALUATION &&
			opType !== OPERATION_TYPE.EVALUATION
		) {
			return [];
		}
		const evaluation = detail.data as unknown as IOperationEvaluationDetail | undefined;
		const docs = evaluation?.document;
		if (!Array.isArray(docs)) {
			return [];
		}
		return docs.map((d) => d?.url).filter((u): u is string => typeof u === 'string' && u.trim().length > 0);
	}

	/**
	 * Abre el visor desde la tarjeta «Media» del detalle de operación (primera imagen seleccionada).
	 * POL: selfies; OCR: anverso, reverso y temporales; evaluación financiera: `document[].url`.
	 * NIF-537: la marca va quemada en píxeles (pwcs); no se añade overlay en el viewer.
	 */
	public buildViewMediaForOperationDetailCard(detail: IOperationDetailResponse): IViewMedia | null {
		if (!detail?.operation_type_name) return null;

		if (detail.operation_type_name === OPERATION_TYPE.POL) {
			const first = detail.data?.images?.selfie?.[0];
			if (!first) return null;
			return this.buildViewMediaSelfieGallery(detail, first);
		}
		if (detail.operation_type_name === OPERATION_TYPE.OCR) {
			const ocr = detail.data as unknown as IOperationOcrDetail;
			const imgs = ocr?.images;
			if (!imgs) return null;

			const urls: string[] = collectOcrAdminImageUrls(imgs);
			if (!urls.length) {
				return null;
			}
			const subtitle = this.buildOcrViewMediaDetailLabel(ocr);
			const tag = {
				label: this.trans.instant('MASTER.STATUS.' + (detail.state_name?.toUpperCase() ?? '')),
				status: this.masterService.getTagStatus(detail.state_name),
			};
			const first = urls[0];
			const data: IMedia[] = urls.map((doc) => ({
				type: 'image' as const,
				doc,
				selected: doc === first,
				scale: 1.0,
			}));
			return {
				show: true,
				title: detail.name ?? '',
				tag,
				subtitle,
				activity: this.buildOperationMediaActivity(detail),
				downloadFileName: `operation_${detail?.uuid ?? 'detail'}_image.jpg`,
				data,
			};
		}
		if (detail.operation_type_name === OPERATION_TYPE.DOCUMENTS) {
			const docsPayload = detail.data as unknown as IOperationDocumentsDetail;
			const urls = (docsPayload?.images?.document ?? []).filter((u): u is string => Boolean(u));
			if (!urls.length) {
				return null;
			}
			const subtitle = this.buildOperationMediaSubtitle(detail);
			const tag = {
				label: this.trans.instant('MASTER.STATUS.' + (detail.state_name?.toUpperCase() ?? '')),
				status: this.masterService.getTagStatus(detail.state_name),
			};
			const first = urls[0];
			const data: IMedia[] = urls.map((doc) => ({
				type: 'image' as const,
				doc,
				selected: doc === first,
				scale: 1.0,
				tag,
			}));
			return {
				show: true,
				title: detail.name ?? '',
				tag,
				subtitle,
				activity: this.buildOperationMediaActivity(detail),
				downloadFileName: `operation_${detail?.uuid ?? 'detail'}_image.jpg`,
				data,
			};
		}
		if (
			detail.operation_type_name === OPERATION_TYPE.CONNECTIONS ||
			detail.operation_type_name === OPERATION_TYPE.FINANCIAL_EVALUATION ||
			detail.operation_type_name === OPERATION_TYPE.EVALUATION
		) {
			const urls = this.getFinancialEvaluationDocumentUrls(detail);
			if (!urls.length) {
				return null;
			}
			const subtitle = this.buildOperationMediaSubtitle(detail);
			const tag = {
				label: this.trans.instant('MASTER.STATUS.' + (detail.state_name?.toUpperCase() ?? '')),
				status: this.masterService.getTagStatus(detail.state_name),
			};
			const uuid = detail.uuid ?? '';
			const short = uuid ? truncateUUID(uuid, 4, 4) : '';
			const detailLabel = uuid ? this.trans.instant('OPERATIONS.DETAIL.MEDIA_OP_ID', { short }) : '';
			const first = urls[0];
			const firstType = resolveViewMediaTypeFromUrl(first);
			const data: IMedia[] = urls.map((doc) => {
				const mediaType = resolveViewMediaTypeFromUrl(doc);
				return {
					type: mediaType,
					doc,
					selected: doc === first,
					scale: 1.0,
					tag,
					...(detailLabel ? { detailLabel } : {}),
					...(mediaType === 'pdf' ? { pdfRenderScale: 1 } : {}),
				};
			});
			return {
				show: true,
				title: detail.name ?? '',
				tag,
				subtitle,
				activity: this.buildOperationMediaActivity(detail),
				downloadFileName: resolveDownloadFileNameFromUrl(
					first,
					`operation_${detail?.uuid ?? 'detail'}`,
					firstType,
				),
				data,
			};
		}
		return null;
	}

	public buildOperationActivityReference(detail: IOperationDetailResponse): string {
		return this.userActivityTracker.shortReference(detail?.uuid || '', 4, 2);
	}

	public buildOperationActivityLink(detail: IOperationDetailResponse, reference?: string): string {
		const path =
			isTfaOperationDetail(detail) && detail.uuid
				? `/operations/detail/${detail.uuid}?entity=tfa`
				: `/operations/detail/${detail.uuid}`;
		return this.userActivityTracker.buildLink(
			path,
			reference || this.buildOperationActivityReference(detail),
		);
	}

	private buildOperationMediaActivity(detail: IOperationDetailResponse) {
		const reference = this.buildOperationActivityReference(detail);

		return {
			type: 'operations',
			text: `Descarga de imágenes capturadas de la operación ${reference}`,
			link: this.buildOperationActivityLink(detail, reference),
			companyUuid: detail?.company_uuid || null,
		};
	}

	private buildOperationMediaSubtitle(detail: IOperationDetailResponse): string {
		const reference = this.buildOperationActivityReference(detail);

		return reference ? this.trans.instant('OPERATIONS.DETAIL.MEDIA_OP_ID', { short: reference }) : '';
	}

	get tabsDetail(): ITab[] {
		return [
			{
				id: TAB_OPERATIONS.DETAILS,
				label: this.trans.instant('OPERATIONS.DETAIL.TABS.DETAILS.TITLE'),
				selected: true,
			},
			{ id: TAB_OPERATIONS.CHECKS, label: this.trans.instant('OPERATIONS.DETAIL.TABS.CHECKS.TITLE') },
			{ id: TAB_OPERATIONS.ACTIVITY, label: this.trans.instant('OPERATIONS.DETAIL.TABS.ACTIVITY.TITLE') },
		];
	}

	private operationNeverStarted(operation: { start_at?: string | null } | null | undefined): boolean {
		const startAt = operation?.start_at;
		if (startAt == null) {
			return true;
		}
		return typeof startAt === 'string' && startAt.trim() === '';
	}

	private pushActivitySectionAndNotStarted(
		detail: IOperationDetailResponse,
		opts: {
			operation: unknown;
			linkInfoTitleKey: string;
			state?: OPERATION_STATUS;
			date?: string | null;
			showLogs?: boolean;
		},
	): void {
		detail.activity?.push({
			description: [this.trans.instant('PROCESSES.DETAIL.OPERATION_NOT_STARTED')],
			...(opts.state
				? {
						tag: {
							label: this.trans.instant(`MASTER.STATUS.${opts.state.toUpperCase()}`),
							status: this.masterService.getTagStatus(opts.state),
						},
					}
				: {}),
			...(opts.date ? { date: opts.date } : {}),
			link: {
				label: this.trans.instant('GENERAL.SHOW_RESPONSE'),
				action: MenuItemAction.SHOW_JSON,
				info: { title: this.trans.instant(opts.linkInfoTitleKey), data: opts.operation },
				...(opts.showLogs ? { showLogs: true } : {}),
			},
		});
	}

	public setActivityLogs(detail: IOperationDetailResponse): void {
		detail.activity = [];
		switch (detail.operation_type_name) {
			case OPERATION_TYPE.OCR:
				this.setOcrActivityLogs(detail);
				break;
			case OPERATION_TYPE.POL:
				this.setPolActivityLogs(detail);
				break;
			case OPERATION_TYPE.DOCUMENTS:
				this.setDocsActivityLogs(detail);
				break;
			case OPERATION_TYPE.CONNECTIONS:
			case OPERATION_TYPE.FINANCIAL_EVALUATION:
			case OPERATION_TYPE.EVALUATION:
				this.setFinancialEvaluationActivityLogs(detail);
				break;
			case OPERATION_TYPE.BORME:
				this.setBormeActivityLogs(detail);
				break;
			case OPERATION_TYPE.FORM:
				this.setFormActivityLogs(detail);
				break;
			case OPERATION_TYPE.TFA:
			case '2FA':
				this.setMessagingActivityLogs(detail);
				break;
			case OPERATION_TYPE.SMS:
			case OPERATION_TYPE.WHATSAPP:
				this.setMessagingActivityLogs(detail);
				break;
		}
	}

	/**
	 * Actividad FORM en detalle de operación (Figma `9257-173875`).
	 * Usa `data.step_activity[]` del back o deriva pasos desde `answers[]`.
	 */
	public setFormActivityLogs(detail: IOperationDetailResponse): void {
		const form = detail.data as unknown as IOperationFormDetail | undefined;
		if (!form) {
			return;
		}

		const startAt = detail.start_at ?? form.start_at;
		if (this.operationNeverStarted({ start_at: startAt })) {
			this.pushActivitySectionAndNotStarted(detail, {
				operation: form,
				linkInfoTitleKey: 'OPERATIONS.DETAIL.FORM.ACTIVITY_JSON_TITLE',
				state: normalizeBormeActivityState(form.state ?? detail.state_name),
				date: detail.end_at ?? form.end_at,
				showLogs: !!detail.logs_url,
			});
			return;
		}

		appendFormOperationActivityLogs(
			detail.activity ?? (detail.activity = []),
			form,
			{
				startAt,
				endAt: detail.end_at ?? form.end_at,
				topLevelState: form.state ?? detail.state_name,
				statusReasons: form.status_reasons ?? detail.status_reasons,
				logsUrl: detail.logs_url,
				locale: this.langSvc.getLanguage(),
			},
			{
				translate: (key, params) => this.trans.instant(key, params),
				shouldDisplayAntifraudCheck: (state, status, options) =>
					this.processHelper.shouldDisplayAntifraudCheck(state, status, options),
				getTagStatus: (state) => this.masterService.getTagStatus(state),
			},
		);
	}

	/**
	 * Actividad de mensajería TFA. Mismo patrón que OCR/BORME: pasos con título + valor, cierre con tag y JSON.
	 */
	public setMessagingActivityLogs(detail: IOperationDetailResponse): void {
		const data = detail.data as IOperationTfaMessagingDetailData | undefined;
		const normalizedState = normalizeMessagingActivityState(detail.state_name);

		if (this.operationNeverStarted({ start_at: detail.start_at })) {
			this.pushActivitySectionAndNotStarted(detail, {
				operation: data,
				linkInfoTitleKey: 'OPERATIONS.DETAIL.MESSAGING.ACTIVITY_JSON_TITLE',
				state: normalizedState,
				date: detail.end_at,
			});
			return;
		}

		appendMessagingActivityLogs(
			detail.activity ?? (detail.activity = []),
			data,
			{
				startAt: detail.start_at,
				endAt: detail.end_at,
				logsUrl: detail.logs_url,
				normalizedState,
			},
			{
				translate: (key, params) => this.trans.instant(key, params),
				getTagStatus: (state) => this.masterService.getTagStatus(state),
			},
		);
	}

	/**
	 * Actividad de operación BORME. `detail.data` sigue el contrato `IOperationBormeDetail`.
	 */
	public setBormeActivityLogs(detail: IOperationDetailResponse): void {
		const borme = detail.data as unknown as IOperationBormeDetail | undefined;
		if (!borme) return;

		if (this.operationNeverStarted({ start_at: detail.start_at ?? borme.start_at })) {
			this.pushActivitySectionAndNotStarted(detail, {
				operation: borme,
				linkInfoTitleKey: 'OPERATIONS.DETAIL.BORME.ACTIVITY_JSON_TITLE',
				state: normalizeBormeActivityState(borme.state),
				date: detail.end_at ?? borme.end_at,
			});
			return;
		}

		appendBormeActivityLogs(
			detail.activity ?? (detail.activity = []),
			borme,
			{
				startAt: detail.start_at ?? borme.start_at,
				endAt: detail.end_at ?? borme.end_at,
				logsUrl: detail.logs_url,
			},
			{
				translate: (key, params) => this.trans.instant(key, params),
				getTagStatus: (state) => this.masterService.getTagStatus(state),
			},
		);
	}

	/**
	 * Registro de actividad OCR en detalle de operación.
	 * Misma lógica que `process-helper.setActivityLogs` (bloque OCR), pero `detail.data` es el payload OCR
	 * (no existe `detail.data.OCR`; fechas suelen venir también en `detail.start_at` / `detail.end_at`).
	 */
	public setOcrActivityLogs(detail: IOperationDetailResponse): void {
		const ocr = detail.data as unknown as IOperationOcrDetail | undefined;
		if (!ocr) return;

		let koGeneral: { title: string; subtitle: string } | null = null;
		if (ocr.actions?.KO) {
			const ocrError = ocr.actions.KO.details?.error;
			koGeneral = this.detailKoGeneral(ocrError?.error_code, undefined, {
				title: ocrError?.title,
				message: ocrError?.message,
				match_data_errors: ocrError?.match_data_errors,
			});
		}

		appendOcrActivityLogs(
			detail.activity ?? (detail.activity = []),
			ocr,
			{
				startAt: detail.start_at ?? ocr.start_at,
				endAt: detail.end_at ?? ocr.end_at,
				logsUrl: detail.logs_url,
				blockedIdentifiersCount: detail.locks?.blocked_identifiers?.length ?? 0,
				koGeneral,
			},
			{
				translate: (key, params) => this.trans.instant(key, params),
				getTagStatus: (state) => this.masterService.getTagStatus(state),
				shouldDisplayAntifraudCheck: (state, status, options) =>
					this.processHelper.shouldDisplayAntifraudCheck(state, status, options),
				canShowOcrImageFraudChecks: () => this.processHelper.canShowOcrImageFraudChecks(),
				getDocumentTypeLabel: (documentType) => this.masterService.getDocumentTypeLabel(documentType),
				getCountryLabel: (country) =>
					this.masterService.getNameCountry(country) || notAvailableLabel(this.trans),
				permanentExpirationLabel: () => permanentExpirationLabel(this.trans),
			},
		);
	}

	/** Misma lógica que en `ProcessHelperService` para errores KO genéricos. */
	public detailKoGeneral(
		code: number,
		polData?: IOperationPolDataTry[],
		payload?: {
			title?: string;
			message?: string;
			msg?: string;
			match_data_errors?: Array<{ key?: string | null }> | null;
		} | null,
	): { title: string; subtitle: string } {
		// NIF-1101: campo concreto (match_data_errors o parse de msg técnico).
		if (code === 2014) {
			const fieldMsg = resolveMatchDataUserMessageFromKoError(
				payload,
				(key, params) => this.trans.instant(key, params),
				(key) => this.masterService.hasTranslation(key),
			);
			if (fieldMsg) {
				return { title: fieldMsg, subtitle: fieldMsg };
			}
		}

		const payloadTitle = payload?.title?.trim() || '';
		const payloadMessage = payload?.message?.trim() || '';
		if (code && (payloadTitle || payloadMessage)) {
			let subtitle = payloadMessage || payloadTitle;
			const polRow = polData?.[2];
			if (code === 2020 && isPolDataRowKoMultipleFaces(polRow)) {
				subtitle = this.trans.instant(`ERRORS.PROCESS.SUBTITLE_${code}_2`);
			}
			return { title: payloadTitle || payloadMessage, subtitle };
		}

		// Sin title/message en payload: i18n (y catálogo en cache si ya estaba cargado por otro flujo).
		if (code) {
			const locale = this.langSvc.currentLang() || 'es';
			const { title, subtitle: baseSubtitle } = this.errorsNumbersService.resolveCopy(
				code,
				locale,
				(key) => this.trans.instant(key),
			);
			let subtitle = baseSubtitle;
			const polRow = polData?.[2];
			if (code === 2020 && isPolDataRowKoMultipleFaces(polRow)) {
				subtitle = this.trans.instant(`ERRORS.PROCESS.SUBTITLE_${code}_2`);
			}
			if (title || subtitle) {
				return { title, subtitle };
			}
		}
		return {
			title: this.trans.instant('ERRORS.PROCESS.TITLE_GENERIC'),
			subtitle: this.trans.instant('ERRORS.PROCESS.SUBTITLE_GENERIC'),
		};
	}

	/**
	 * Registro de actividad POL en detalle de operación.
	 * Alineado con `process-helper.setActivityLogs` (bloque POL): `detail.data` es el payload POL (no `detail.data.POL`).
	 */
	public setPolActivityLogs(detail: IOperationDetailResponse): void {
		const pol = detail.data as unknown as IOperationPolDetail | undefined;
		if (!pol) return;

		let koGeneral: { title: string; subtitle: string } | null = null;
		if (pol.actions?.KO) {
			const polError = pol.actions.KO.details?.error;
			koGeneral = this.detailKoGeneral(polError?.error_code, pol.data, {
				title: polError?.title,
				message: polError?.message,
			});
		}

		appendPolActivityLogs(
			detail.activity ?? (detail.activity = []),
			pol,
			{
				startAt: detail.start_at ?? pol.start_at,
				endAt: detail.end_at ?? pol.end_at,
				logsUrl: detail.logs_url,
				blockedFacesCount: detail.locks?.blocked_faces?.length ?? 0,
				koGeneral,
			},
			{
				translate: (key, params) => this.trans.instant(key, params),
				getTagStatus: (state) => this.masterService.getTagStatus(state),
				shouldDisplayAntifraudCheck: (state, status, options) =>
					this.processHelper.shouldDisplayAntifraudCheck(state, status, options),
			},
		);
	}

	/**
	 * Actividad de operación DOCUMENTS. En detalle de operación `detail.data` es el payload (no `detail.data.DOCUMENTS`).
	 */
	public setDocsActivityLogs(detail: IOperationDetailResponse): void {
		const docs = detail.data as unknown as IOperationDocumentsDetail;
		if (!docs) return;

		appendDocumentsActivityLogs(
			detail.activity ?? (detail.activity = []),
			docs,
			{
				startAt: detail.start_at ?? docs.start_at,
				endAt: detail.end_at ?? docs.end_at,
				logsUrl: detail.logs_url,
			},
			{
				translate: (key, params) => this.trans.instant(key, params),
				hasTranslation: (key) => this.masterService.hasTranslation(key),
				getTagStatus: (state) => this.masterService.getTagStatus(state),
			},
		);
	}

	/**
	 * Actividad de validación financiera / evaluación. En detalle de operación `detail.data` es `IOperationEvaluationDetail`
	 * (en proceso va en `detail.data.EVALUATION`).
	 */
	public setFinancialEvaluationActivityLogs(detail: IOperationDetailResponse): void {
		const evaluation = detail.data as unknown as IOperationEvaluationDetail;
		if (!evaluation) return;

		if (this.operationNeverStarted({ start_at: detail.start_at ?? evaluation.start_at })) {
			this.pushActivitySectionAndNotStarted(detail, {
				operation: evaluation,
				linkInfoTitleKey: 'PROCESSES.DETAIL.EVALUATION',
				state: evaluation.state,
				date: detail.end_at ?? evaluation.end_at,
			});
			return;
		}

		appendFinancialEvaluationActivityLogs(
			detail.activity ?? (detail.activity = []),
			evaluation,
			{
				startAt: detail.start_at ?? evaluation.start_at,
				endAt: detail.end_at ?? evaluation.end_at,
				logsUrl: detail.logs_url,
			},
			{
				translate: (key, params) => this.trans.instant(key, params),
				hasTranslation: (key) => this.masterService.hasTranslation(key),
				getTagStatus: (state) => this.masterService.getTagStatus(state),
				notAvailableLabel: () => notAvailableLabel(this.trans),
			},
		);
	}
}
